<template>
  <section id="about" class="about bg-decoration">
    <div class="container">
      <div class="about-content">
        <div class="about-text">
          <SectionHeader
            subtitle-key="about.subtitle"
            title-key="about.title"
            description-key="about.description1"
          />
          <p class="about-description">
            {{ $t('about.description2') }}
          </p>
          <div class="stats">
            <div class="stat-item glass glass-glow" v-for="(stat, index) in stats" :key="index">
              <div class="stat-icon glass-sm">
                <span v-html="stat.icon"></span>
              </div>
              <div class="stat-content">
                <span class="stat-number">{{ $t(`about.statNumbers.${stat.key}`) }}</span>
                <span class="stat-label">{{ $t(`about.stats.${stat.key}`) }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="about-image">
          <div class="carousel-container glass glass-glow">
            <div class="carousel" :style="{ transform: `translateX(-${currentIndex * 100}%)` }">
              <div class="carousel-item" v-for="(slide, index) in images" :key="index">
                <img :src="slide.src" :alt="$t(slide.altKey)" />
              </div>
            </div>
            <div class="carousel-indicators" role="tablist">
              <button
                v-for="index in imageIndexes"
                :key="index"
                class="indicator"
                :class="{ active: currentIndex === index }"
                @click="currentIndex = index"
                role="tab"
                :aria-selected="currentIndex === index"
                :aria-label="$t('about.carouselLabel', { index: index + 1 })"
                :tabindex="currentIndex === index ? 0 : -1"
              ></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import SectionHeader from './common/SectionHeader.vue'
import { getIcon } from '@/assets/icons.js'

const currentIndex = ref(0)
const images = ref([
  { src: require('../../.image/about/about1.png'), altKey: 'about.carouselAlt.1' },
  { src: require('../../.image/about/about2.png'), altKey: 'about.carouselAlt.2' },
  { src: require('../../.image/about/about3.png'), altKey: 'about.carouselAlt.3' }
])
const imageIndexes = computed(() => Array.from(images.value.keys()))

const stats = ref([
  { key: 'experience', icon: getIcon('growth') },
  { key: 'clients', icon: getIcon('impact') },
  { key: 'consultants', icon: getIcon('delivery') },
  { key: 'satisfaction', icon: getIcon('culture') }
])

let intervalId = null

const nextSlide = () => {
  currentIndex.value = (currentIndex.value + 1) % images.value.length
}

onMounted(() => {
  intervalId = setInterval(nextSlide, 4000)
})

onUnmounted(() => {
  if (intervalId) {
    clearInterval(intervalId)
  }
})
</script>

<style scoped>
.about {
  padding: 120px 0;
  background: linear-gradient(135deg, color-mix(in srgb, var(--white) 98%, var(--secondary-color)) 0%, var(--bg-light) 50%, color-mix(in srgb, var(--secondary-color) 5%, var(--bg-light)) 100%);
  position: relative;
}

.about::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background:
    radial-gradient(circle at 15% 20%, color-mix(in srgb, var(--secondary-color) 12%, transparent) 0%, transparent 50%),
    radial-gradient(circle at 85% 80%, color-mix(in srgb, var(--primary-color) 8%, transparent) 0%, transparent 50%);
  pointer-events: none;
}

@media (max-width: 1366px) {
  .about {
    padding: 100px 0;
  }
}

@media (max-width: 1024px) {
  .about {
    padding: 80px 0;
  }
}

@media (max-width: 768px) {
  .about {
    padding: 60px 0;
  }
}

.about-content {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 80px;
  align-items: center;
  position: relative;
  z-index: 1;
}

@media (max-width: 1366px) {
  .about-content {
    gap: 60px;
  }
}

@media (max-width: 1024px) {
  .about-content {
    grid-template-columns: 1fr 1fr;
    gap: 40px;
  }
}

@media (max-width: 768px) {
  .about-content {
    grid-template-columns: 1fr;
    gap: 40px;
  }
}

.about-image {
  position: relative;
}

@media (max-width: 768px) {
  .about-image {
    order: -1;
  }
}

.carousel-container {
  width: 100%;
  aspect-ratio: 1;
  background: linear-gradient(135deg, color-mix(in srgb, var(--white) 50%, transparent) 0%, color-mix(in srgb, var(--white) 30%, transparent) 100%);
  border-radius: var(--rounded-lg); /* 设计系统大圆角 */
  position: relative;
  overflow: hidden;
}

@media (max-width: 768px) {
  .carousel-container {
    aspect-ratio: 16/9;
  }
}

.carousel {
  display: flex;
  width: 100%;
  height: 100%;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.carousel-item {
  flex: 0 0 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.carousel-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: var(--rounded-lg); /* 设计系统大圆角 */
}

.carousel-indicators {
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 4px;
  z-index: 10;
}

.indicator {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  padding: 0;
  font-size: 0;
  line-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.indicator::before {
  content: '';
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: color-mix(in srgb, var(--white) 60%, transparent);
  box-shadow: 0 2px 8px color-mix(in srgb, var(--primary-color) 15%, transparent);
  transition: all 0.3s ease;
}

.indicator:hover::before {
  background: color-mix(in srgb, var(--white) 90%, transparent);
  transform: scale(1.2);
}

.indicator.active::before {
  background: var(--secondary-color);
  width: 32px;
  border-radius: 6px;
  box-shadow: 0 2px 12px color-mix(in srgb, var(--secondary-color) 40%, transparent);
}

.about-description {
  color: var(--text-light);
  font-size: clamp(1rem, 1.5vw, 1.1rem); /* 设计系统body级字体 */
  line-height: 1.8;
  margin-bottom: var(--spacing-sm);
  text-align: left;
  max-width: 65ch; /* 符合设计系统行宽规则 */
}

@media (max-width: 1366px) {
  .about-description {
    font-size: 1rem;
    line-height: 1.7;
  }
}

@media (max-width: 1024px) {
  .about-description {
    font-size: 0.95rem;
  }
}

@media (max-width: 768px) {
  .about-description {
    font-size: 0.9rem;
    text-align: left;
    margin-bottom: 16px;
  }
}

.stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-top: 40px;
}

@media (max-width: 1366px) {
  .stats {
    gap: 16px;
    margin-top: 32px;
  }
}

@media (max-width: 1024px) {
  .stats {
    gap: 14px;
    margin-top: 28px;
  }
}

@media (max-width: 768px) {
  .stats {
    gap: 12px;
    margin-top: 24px;
  }
}

@media (max-width: 480px) {
  .stats {
    grid-template-columns: 1fr;
  }
}

.about-text {
  display: flex;
  flex-direction: column;
  position: relative;
}

.about-text :deep(.section-header) {
  text-align: left;
  margin-bottom: 24px;
}

.about-text :deep(.section-description) {
  text-align: left;
  margin-left: 0;
  max-width: 100%;
}

@media (max-width: 768px) {
  .about-text :deep(.section-header) {
    text-align: left;
  }

  .about-text :deep(.section-description) {
    text-align: left;
  }
}

.stat-item {
  display: flex;
  gap: var(--spacing-xs);
  align-items: center;
  padding: var(--spacing-sm);
  border-radius: var(--rounded-md); /* 设计系统中等圆角 */
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.stat-item:hover {
  transform: translateY(-4px);
}

.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: var(--rounded-md); /* 设计系统中等圆角 */
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-blue); /* 设计系统标准命名 */
  flex-shrink: 0;
}

.stat-icon svg {
  width: 28px;
  height: 28px;
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-number {
  font-size: 2rem;
  font-weight: 700;
  color: var(--primary-blue); /* 设计系统标准命名 */
  line-height: 1.1;
  margin-bottom: 4px;
}

@media (max-width: 1366px) {
  .stat-number {
    font-size: 1.8rem;
  }
}

@media (max-width: 1024px) {
  .stat-number {
    font-size: 1.6rem;
  }
}

@media (max-width: 768px) {
  .stat-number {
    font-size: 1.5rem;
  }
}

@media (max-width: 375px) {
  .stat-number {
    font-size: 1.4rem;
  }
}

.stat-label {
  font-size: 0.95rem;
  color: var(--text-light);
  font-weight: 500;
}

@media (max-width: 768px) {
  .stat-label {
    font-size: 0.85rem;
  }
}

@media (max-width: 375px) {
  .stat-label {
    font-size: 0.8rem;
  }
}
</style>
